import React, { useState, useRef } from "react";
import * as pdfjsLib from "pdfjs-dist";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Document, Packer, Paragraph, PageBreak } from "docx";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export default function PDFToWordExcel({ file }) {
  // Add missing downloadWord function
  const downloadWord = async () => {
    // Create a docx file with each page's text on a new page, preserving line breaks
    const children = [];
    pages.forEach((pageText, idx) => {
      if (idx > 0) children.push(new PageBreak());
      const lines = pageText.split("\n");
      lines.forEach(line => {
        children.push(new Paragraph(line));
      });
    });
    const doc = new Document({
      sections: [
        {
          children
        }
      ]
    });
    const blob = await Packer.toBlob(doc);
    saveAs(blob, "converted.docx");
  };
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const cancelRef = useRef(false);

  // Add missing handleCancel function
  const handleCancel = () => {
    cancelRef.current = true;
    setLoading(false);
  };

  // Add missing handleConvert function
  const handleConvert = async () => {
    if (!file) return;
    setLoading(true);
    setPages([]);
    cancelRef.current = false;
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const pageTexts = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      if (cancelRef.current) {
        setLoading(false);
        return;
      }
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      // Try to preserve line breaks by grouping items by y position
      const lines = [];
      let lastY = null;
      let currentLine = [];
      content.items.forEach(item => {
        if (lastY === null) lastY = item.transform[5];
        if (Math.abs(item.transform[5] - lastY) > 2) {
          lines.push(currentLine);
          currentLine = [];
          lastY = item.transform[5];
        }
        currentLine.push(item.str);
      });
      if (currentLine.length) lines.push(currentLine);
      pageTexts.push(lines.map(line => line.join(" ")).join("\n"));
    }
    setPages(pageTexts);
    setLoading(false);
  };

  React.useEffect(() => {
    if (!file) {
      setPages([]);
      setLoading(false);
    }
    // Don't auto-convert on file change
    // eslint-disable-next-line
  }, [file]);

  const downloadExcel = () => {
    const rows = pages.flatMap(page => page.split("\n").map(line => [line]));
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), "converted.xlsx");
  };

  return (
    <div>
      {!file && (
        <div style={{ color: '#c00', fontSize: 17, margin: '24px 0', textAlign: 'center', fontWeight: 500 }}>
          No file selected. Please upload a PDF file to begin.
        </div>
      )}
      {file && !loading && pages.length === 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={handleConvert} style={{ padding: '10px 24px', fontSize: 16, borderRadius: 8, background: '#007bff', color: '#fff', border: 'none', marginBottom: 16 }}>Convert</button>
          <button
            onClick={() => {
              // Remove file by triggering parent state reset if available
              if (typeof window !== 'undefined') {
                const input = document.getElementById('pdf-upload');
                if (input) input.value = null;
              }
              if (typeof window.setFile === 'function') {
                window.setFile(null);
              }
            }}
            style={{ color: '#c00', background: 'none', border: 'none', fontWeight: 'bold', fontSize: 18, cursor: 'pointer' }}
            title="Remove file"
          >×</button>
        </div>
      )}
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>Extracting text...</span>
          <button onClick={handleCancel} style={{ color: 'red' }}>Cancel</button>
        </div>
      )}
      {pages.length > 0 && (
        <div>
          <button onClick={downloadWord}>Download Word (.docx)</button>
          <button onClick={downloadExcel} style={{ marginLeft: 8 }}>Download Excel (.xlsx)</button>
          <button
            onClick={() => {
              if (typeof window !== 'undefined') {
                const input = document.getElementById('pdf-upload');
                if (input) input.value = null;
              }
              if (typeof window.setFile === 'function') {
                window.setFile(null);
              }
            }}
            style={{ color: '#c00', background: 'none', border: 'none', fontWeight: 'bold', fontSize: 18, cursor: 'pointer', marginLeft: 8 }}
            title="Remove file"
          >×</button>
          <div style={{ marginTop: 16 }}>
            {pages.map((page, idx) => (
              <div key={idx} style={{ marginBottom: 12 }}>
                <strong>Page {idx + 1}:</strong>
                <pre style={{ background: '#f4f4f4', padding: 8, borderRadius: 6 }}>{page}</pre>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
